"use client"

import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowDown, ArrowUp, ChevronDown } from "lucide-react"
import { Dispatch, SetStateAction, useEffect, useState } from "react"

import { CorrectFlagIcon } from "@/lib/apps/tsp/correct-flag"
import { useSideBarHead } from "@/lib/stores/sidebarHead"
import { cn } from "@/lib/utils"

import LoadingSpinner from "@/components/nango/common/components/loading-spinner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { useIsMobile } from "@/hooks/use-mobile"
import type { Result } from "@/nango/types/apps.tsp/race/Race"

const columnsToDisplayName: Record<string, string> = {
  result: "Result",
  full_name: "Athlete",
  bib: "Bib",
  total_points: "Total Pts.",
  time: "Time",
  time_points: "Time Pts.",
  top_air_trick: "Top Air",
  top_air_coefficient: "Top Coeff.",
  top_air_points: "Top Pts.",
  top_air_execution: "Top Exc.",
  top_air_judge1: "Top J6",
  top_air_judge2: "Top J7",
  bottom_air_trick: "Bot. Air",
  bottom_air_coefficient: "Bot. Coeff.",
  bottom_air_points: "Bot. Pts.",
  bottom_air_execution: "Bot. Exc.",
  bottom_air_judge1: "Bot. J6",
  bottom_air_judge2: "Bot. J7",
  air_points: "Air Pts.",
  ski_base: "Ski Base",
  ski_deduction_total: "Ski Ded.",
  ski_points: "Ski Pts.",
}
// Columns kept visible on mobile; every other visible column is hidden to
// avoid horizontal scroll on small screens.
const mobileHiddenColumns: VisibilityState = {
  time: false,
  time_points: false,
  top_air_trick: false,
  bottom_air_trick: false,
  air_points: false,
  ski_base: false,
  ski_points: false,
}
const reverseSortedColumns = ["time", "result", "bib"]
const stringColumns = ["full_name", "top_air_trick", "bottom_air_trick"]
const relativeExclude = [
  ...stringColumns,
  "id",
  "athlete",
  "athlete_last_name",
  "athlete_first_name",
  "country",
  "is_rank",
  "run",
  "result",
  "race_points",
  "tie",
  "bib",
]

// Numeric Result keys mutated when computing the "Relative" view deltas.
type RelativeKey =
  | "total_points"
  | "time"
  | "time_points"
  | "top_air_coefficient"
  | "top_air_points"
  | "top_air_execution"
  | "top_air_judge1"
  | "top_air_judge2"
  | "bottom_air_coefficient"
  | "bottom_air_points"
  | "bottom_air_execution"
  | "bottom_air_judge1"
  | "bottom_air_judge2"
  | "air_points"
  | "ski_base"
  | "ski_deduction_total"
  | "ski_points"

function BestAthlete({
  result,
  property,
  title,
}: {
  result: Result | undefined
  property: keyof Result
  title: string
}) {
  if (!result) return null
  const raw = result[property]
  const value =
    typeof raw === "number"
      ? Number.isInteger(raw)
        ? raw
        : Number(raw.toFixed(2))
      : raw
  return (
    <Card className="py-0 transition-shadow hover:shadow-md">
      <CardContent className="flex items-center gap-3 p-3.5">
        <CorrectFlagIcon
          country={result.country}
          className="ring-border size-9 shrink-0 rounded-full ring-1"
        />
        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-sm font-semibold">
            {result.athlete_last_name} {result.athlete_first_name}
          </p>
          <p className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
            {title}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-primary font-mono text-sm font-bold tabular-nums">
            {value}
          </p>
          <p className="text-muted-foreground text-[10px] tracking-wide uppercase">
            pts
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export function BestAthletesByCategory({
  results,
}: {
  results: Result[] | undefined
}) {
  const bestOverAll = (results || [])
    .slice()
    .sort((a, b) => (b.total_points ?? 0) - (a.total_points ?? 0))[0]
  const bestTopAir = (results || [])
    .slice()
    .sort((a, b) => (b.top_air_points ?? 0) - (a.top_air_points ?? 0))[0]
  const bestBottomAir = (results || [])
    .slice()
    .sort((a, b) => (b.bottom_air_points ?? 0) - (a.bottom_air_points ?? 0))[0]
  const bestAir = (results || [])
    .slice()
    .sort((a, b) => (b.air_points ?? 0) - (a.air_points ?? 0))[0]
  const bestSki = (results || [])
    .slice()
    .sort((a, b) => (b.ski_points ?? 0) - (a.ski_points ?? 0))[0]
  const bestSkiBase = (results || [])
    .slice()
    .sort((a, b) => (b.ski_base ?? 0) - (a.ski_base ?? 0))[0]
  const bestSkiDeductions = (results || [])
    .filter((res) => res.ski_deduction_total !== null)
    .sort(
      (a, b) => (b.ski_deduction_total ?? 0) - (a.ski_deduction_total ?? 0),
    )[0]
  const bestTime = (results || [])
    .slice()
    .sort((a, b) => (b.time_points ?? 0) - (a.time_points ?? 0))[0]

  return (
    <div className="grid grid-cols-1 gap-3 py-6 sm:grid-cols-2 xl:grid-cols-4">
      <BestAthlete
        result={bestOverAll}
        property={"total_points"}
        title="Overall"
      />
      <BestAthlete
        result={bestTopAir}
        property={"top_air_points"}
        title="Top air"
      />
      <BestAthlete
        result={bestBottomAir}
        property={"bottom_air_points"}
        title="Bottom air"
      />
      <BestAthlete result={bestAir} property={"air_points"} title="Air Total" />
      <BestAthlete result={bestTime} property={"time_points"} title="Time" />
      <BestAthlete
        result={bestSkiBase}
        property={"ski_base"}
        title="Ski base"
      />
      <BestAthlete
        result={bestSkiDeductions}
        property={"ski_deduction_total"}
        title="Deductions"
      />
      <BestAthlete result={bestSki} property={"ski_points"} title="Best ski" />
    </div>
  )
}

export function SortButton({
  column,
  title,
}: {
  column: Column<Result, unknown>
  title: string
}) {
  return (
    <Button
      variant="link"
      onClick={() => {
        if (reverseSortedColumns.includes(column.id)) {
          column.toggleSorting(
            column.getIsSorted() ? column.getIsSorted() === "asc" : false,
          )
        } else {
          column.toggleSorting(
            column.getIsSorted() ? column.getIsSorted() === "asc" : true,
          )
        }
      }}
      className="-mx-4"
    >
      {columnsToDisplayName[title]}
      {column.getIsSorted() ? (
        column.getIsSorted() === "asc" ? (
          <ArrowDown className="ml-2 size-4" />
        ) : (
          <ArrowUp className="ml-2 size-4" />
        )
      ) : (
        <ArrowDown className="ml-2 size-4 opacity-0" />
      )}
    </Button>
  )
}

function ColumnHeader(
  title: string,
  viewMode: (typeof viewModes)[number],
  sorting: SortingState,
): ColumnDef<Result> {
  const sideBarHead = useSideBarHead()
  const proMode = viewMode === "Sort"
  return {
    accessorKey: title,
    sortingFn: (a, b) => {
      const sortingBy = (sorting[0]?.id || "total_points") as keyof Result
      if (a.getValue(sortingBy) === null && b.getValue(sortingBy) !== null) {
        return reverseSortedColumns.includes(title) ? 1 : -1
      }
      if (b.getValue(sortingBy) === null && a.getValue(sortingBy) !== null) {
        return reverseSortedColumns.includes(title) ? -1 : 1
      }
      if (stringColumns.includes(sortingBy)) {
        return (a.getValue(sortingBy) as string).localeCompare(
          b.getValue(sortingBy) as string,
        )
      }
      return Number(a.getValue(sortingBy)) - Number(b.getValue(sortingBy))
    },
    header: ({ column }) => {
      return (
        <>
          {proMode ? (
            <SortButton column={column} title={title} />
          ) : (
            <>{columnsToDisplayName[title]}</>
          )}
        </>
      )
    },
    cell: ({ row }) => {
      return (
        <>
          {row.original === null ? (
            <Skeleton className="h-4 w-10"></Skeleton>
          ) : (
            <div
              className={cn(
                row.original.athlete === sideBarHead.data?.id
                  ? "font-semibold"
                  : "",
                viewMode === "Relative" &&
                  Number(row.getValue(title)) === row.getValue(title) &&
                  row.getValue(title) !== 0 &&
                  !relativeExclude.includes(title)
                  ? (row.getValue(title) as number) > 0
                    ? reverseSortedColumns.includes(title)
                      ? "text-destructive"
                      : "text-positive"
                    : reverseSortedColumns.includes(title)
                      ? "text-positive"
                      : "text-destructive"
                  : "",
              )}
            >
              {title === "full_name" ? (
                <div className="flex items-center gap-2">
                  <CorrectFlagIcon
                    country={(row.getValue(title) as string).slice(1, 4)}
                    className="size-5"
                  />
                  {(row.getValue(title) as string).slice(6)}
                </div>
              ) : (
                row.getValue(title)
              )}
              {viewMode === "Relative" &&
                !relativeExclude.includes(title) &&
                row.getValue(title) !== null &&
                "%"}
            </div>
          )}
        </>
      )
    },
  }
}

export const viewModes = ["Default", "Sort", "Rank", "Relative"] as const

export default function RunTable({
  results,
  filter = true,
  showProMode,
  isCoach = false,
  isAthlete = false,
  isLeaderboard = false,
  viewMode,
  setViewMode,
}: {
  results: Result[] | undefined
  filter?: boolean
  showProMode: boolean
  isCoach?: boolean
  isAthlete?: boolean
  isLeaderboard?: boolean
  viewMode: (typeof viewModes)[number]
  setViewMode: Dispatch<SetStateAction<(typeof viewModes)[number]>>
}) {
  const isMobile = useIsMobile()
  const [sorting, setSorting] = useState<SortingState>([
    { desc: false, id: "result" },
  ])
  const sideBarHead = useSideBarHead()
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    bib: false,
    top_air_coefficient: false,
    top_air_points: false,
    top_air_execution: false,
    top_air_judge1: false,
    top_air_judge2: false,
    bottom_air_coefficient: false,
    bottom_air_points: false,
    bottom_air_execution: false,
    bottom_air_judge1: false,
    bottom_air_judge2: false,
    ski_deduction_total: false,
  })
  const [rowSelection, setRowSelection] = useState({})
  const [filteredResults, setFilteredResults] = useState<Result[]>([])
  const columns: ColumnDef<Result>[] = Object.keys(columnsToDisplayName).map(
    (key) => ColumnHeader(key, viewMode, sorting),
  )
  useEffect(() => {
    const sortedResults = (
      results?.filter((result) => !result.is_rank) || []
    ).sort((a, b) => {
      return (a.result ?? 0) - (b.result ?? 0)
    })
    const sortedResultsRank = (
      results?.filter((result) => result.is_rank) || []
    ).sort((a, b) => {
      return (a.result ?? 0) - (b.result ?? 0)
    })
    const headerAthleteResults = sortedResults.find(
      (res) => res.athlete === sideBarHead.data?.id,
    )

    const res: Result[] = sortedResults.filter(
      (r) => !filter || !r.is_duplicate,
    )

    setFilteredResults(
      viewMode === "Rank"
        ? res
            .map((result) =>
              sortedResultsRank.find((r) => r.result === result.result),
            )
            .filter((r): r is Result => r !== undefined)
        : viewMode === "Relative"
          ? headerAthleteResults &&
            (headerAthleteResults.result ?? 0) <= res.length
            ? res.map((res) => {
                const newRes = { ...res }
                for (const key of Object.keys(newRes)) {
                  if (
                    relativeExclude.includes(key) ||
                    newRes[key as keyof Result] === null ||
                    newRes[key as keyof Result] === 0
                  ) {
                    continue
                  }

                  newRes[key as RelativeKey] =
                    Math.floor(
                      (((headerAthleteResults[key as keyof Result] as number) -
                        (newRes[key as keyof Result] as number)) /
                        (newRes[key as keyof Result] as number)) *
                        1000,
                    ) / 10
                }
                return newRes
              })
            : []
          : res,
    )
  }, [filter, results, sideBarHead, viewMode])

  useEffect(() => {
    const mobileOverrides = isMobile ? mobileHiddenColumns : {}
    if (viewMode === "Rank") {
      setColumnVisibility({
        bib: false,
        top_air_coefficient: false,
        top_air_trick: false,
        top_air_execution: false,
        top_air_judge1: false,
        top_air_judge2: false,
        bottom_air_coefficient: false,
        bottom_air_trick: false,
        bottom_air_execution: false,
        bottom_air_judge1: false,
        bottom_air_judge2: false,
        ski_deduction_total: false,
        ...mobileOverrides,
      })
    } else if (viewMode === "Relative") {
      setColumnVisibility({
        bib: false,
        top_air_coefficient: false,
        top_air_trick: false,
        top_air_execution: false,
        top_air_judge1: false,
        top_air_judge2: false,
        bottom_air_coefficient: false,
        bottom_air_trick: false,
        bottom_air_execution: false,
        bottom_air_judge1: false,
        bottom_air_judge2: false,
        ski_deduction_total: false,
        ...mobileOverrides,
      })
    } else if (viewMode === "Default") {
      setColumnVisibility({
        bib: false,
        top_air_coefficient: false,
        top_air_points: false,
        top_air_execution: false,
        top_air_judge1: false,
        top_air_judge2: false,
        bottom_air_coefficient: false,
        bottom_air_points: false,
        bottom_air_execution: false,
        bottom_air_judge1: false,
        bottom_air_judge2: false,
        ski_deduction_total: false,
        ...mobileOverrides,
      })
    } else {
      setColumnVisibility({
        bib: false,
        top_air_coefficient: false,
        top_air_trick: false,
        top_air_execution: false,
        top_air_judge1: false,
        top_air_judge2: false,
        bottom_air_coefficient: false,
        bottom_air_trick: false,
        bottom_air_execution: false,
        bottom_air_judge1: false,
        bottom_air_judge2: false,
        ski_deduction_total: false,
        ...mobileOverrides,
      })
    }

    setSorting([{ desc: false, id: "result" }])
  }, [viewMode, isMobile])

  const table = useReactTable({
    data: filteredResults,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })
  return (
    <div className="w-full">
      <div className="flex items-center py-4">
        <Input
          placeholder="Filter athletes..."
          value={
            (table.getColumn("full_name")?.getFilterValue() as string) ?? ""
          }
          onChange={(event) =>
            table.getColumn("full_name")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />

        <div className="ml-auto flex items-center gap-2">
          {showProMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="">
                  View: {viewMode} <ChevronDown className="ml-2 size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="max-h-60 overflow-scroll"
              >
                {viewModes.map((mode, index) => {
                  if ((isAthlete || isCoach) && mode === "Default") {
                    return null
                  }
                  return mode === "Sort" ? (
                    <DropdownMenuCheckboxItem
                      key={index}
                      checked={viewMode === mode}
                      onClick={() => {
                        setViewMode(mode)
                      }}
                      disabled={!isAthlete && !isCoach}
                      className="flex justify-between gap-2"
                    >
                      {mode}
                      <Badge>Athlete</Badge>
                    </DropdownMenuCheckboxItem>
                  ) : (
                    <DropdownMenuCheckboxItem
                      key={index}
                      checked={viewMode === mode}
                      onClick={() => {
                        setViewMode(mode)
                      }}
                      disabled={
                        (!isCoach && mode !== "Default") ||
                        (isLeaderboard && mode === "Rank") ||
                        (mode === "Relative" &&
                          filteredResults.find(
                            (e) => e.athlete === sideBarHead.data?.id,
                          ) === undefined)
                      }
                      className="flex justify-between gap-2"
                    >
                      {mode}
                      {mode !== "Default" && <Badge>Coach</Badge>}
                    </DropdownMenuCheckboxItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="">
                Columns <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-60 overflow-scroll"
            >
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onClick={(e) => {
                        e.preventDefault()
                        column.toggleVisibility(!column.getIsVisible())
                      }}
                    >
                      {columnsToDisplayName[column.id] || column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {viewMode === "Sort" && (
                  <TableHead className="w-5">Ord.</TableHead>
                )}
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="text-nowrap">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            <>
              {results === undefined ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex w-full items-center justify-center py-20">
                      <LoadingSpinner />
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const sortingBy = (sorting[0]?.id ||
                    "total_points") as keyof Result
                  const rank = filteredResults
                    .filter((r) => r[sortingBy] !== null)
                    .sort((a, b) => {
                      if (reverseSortedColumns.includes(sortingBy)) {
                        return (
                          (a[sortingBy] as number) - (b[sortingBy] as number)
                        )
                      }
                      return (b[sortingBy] as number) - (a[sortingBy] as number)
                    })
                    .findIndex(
                      (result) => result[sortingBy] === row.original[sortingBy],
                    )

                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {viewMode === "Sort" && (
                        <TableCell className="w-5">
                          {sortingBy === "full_name" ||
                          sortingBy === "top_air_trick" ||
                          sortingBy === "bottom_air_trick"
                            ? row.index + 1
                            : rank + 1}
                        </TableCell>
                      )}
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="max-w-52 text-nowrap"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </>
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="text-muted-foreground flex-1 text-sm">
          {table.getFilteredRowModel().rows.length} athletes(s) finished.
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
      <h3 className="text-xl font-bold">{"Best athletes"}</h3>
      <BestAthletesByCategory
        results={results?.filter(
          (r) =>
            r.is_rank === false && (isLeaderboard ? true : !r.is_duplicate),
        )}
      />
    </div>
  )
}
